import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { api, ApiError, type Permission, type User } from '@/lib/api'
import { tokenStore } from '@/lib/tokenStore'

interface AuthState {
  user: User | null
  loading: boolean
  /** True when the caller holds the given permission. */
  can: (permission: Permission) => boolean
  /** Convenience for the common "may change things" check. */
  canEdit: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    // Asks the server who we are rather than trusting the stored copy.
    api
      .me()
      .then((res) => {
        if (!cancelled) setUser(res.user)
      })
      .catch((err) => {
        // A 401 on first load simply means "not signed in" and is expected.
        if (!(err instanceof ApiError) || err.status !== 401) {
          console.error('Failed to resolve session', err)
        }
        tokenStore.clear()
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    const auth = await api.login(username, password)
    // The server stores nothing, so the tokens are kept here and presented on
    // each subsequent request.
    tokenStore.write(auth)
    setUser(auth.user)
  }, [])

  const logout = useCallback(async () => {
    await api.logout().catch(() => null)
    tokenStore.clear()
    setUser(null)
  }, [])

  const value = useMemo<AuthState>(
    () => {
      const can = (permission: Permission) => user?.permissions?.includes(permission) ?? false
      return {
        user,
        loading,
        can,
        // Upload is the representative write action; finer checks use can().
        canEdit: can('upload'),
        login,
        logout,
      }
    },
    [user, loading, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside an AuthProvider')
  return context
}
